import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Chip,
  Popover,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import { AppleStyleSpinner } from "./MemCreation/AppleStyleSpinner";
import CreateMOUModal from "./CreateMOUModal";
import ExhibitAModal from "./ExhibitAModal";

const ViewMOUModal = ({ open, onClose, instituteId, membershipId }) => {
  const [mous, setMous] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signingMouId, setSigningMouId] = useState(null);
  const [createMOUOpen, setCreateMOUOpen] = useState(false);

  // PDF Preview Modal states
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfBase64, setPdfBase64] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [currentMouNumber, setCurrentMouNumber] = useState("");
  const [currentMouId, setCurrentMouId] = useState(null);

  // New states for popover and SPOC selection
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEducatorId, setSelectedEducatorId] = useState("");
  const [availableSpocs, setAvailableSpocs] = useState([]);
  const [loadingSpocs, setLoadingSpocs] = useState(false);
  const [sendingMouId, setSendingMouId] = useState(null);
  const [spocMouId, setSpocMouId] = useState(null);

  // Exhibit A modal states
  const [exhibitAModalOpen, setExhibitAModalOpen] = useState(false);
  const [selectedExhibitContent, setSelectedExhibitContent] = useState("");
  const [selectedMouId, setSelectedMouId] = useState(null);

  useEffect(() => {
    if (open && instituteId) {
      fetchMOUs();
    }
  }, [open, instituteId]);

  // Cleanup blob URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const fetchMOUs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/accounts/pending-mous`, {
        params: {
          institute_id: instituteId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setMous(response.data.pending_mous || []);
    } catch (error) {
      console.error("Error fetching MOUs:", error);
      setMous([]);
    } finally {
      setLoading(false);
    }
  };

  // Convert base64 to Blob URL
  const base64ToBlob = (base64String) => {
    try {
      // Decode base64 string
      const binaryString = window.atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob
      const blob = new Blob([bytes], { type: "application/pdf" });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error converting base64 to blob:", error);
      return null;
    }
  };

  // Fetch MOU PDF for preview
  const handleViewMOUPDF = async (mouId, mouNumber) => {
    setLoadingPdf(true);
    setPdfPreviewOpen(true);
    setCurrentMouNumber(mouNumber);
    setCurrentMouId(mouId);

    // Cleanup previous blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl("");
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/spoc/download-mou/${mouId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const { pdf_base64 } = response.data;

      if (pdf_base64) {
        setPdfBase64(pdf_base64);
        const blobUrl = base64ToBlob(pdf_base64);
        if (blobUrl) {
          setPdfBlobUrl(blobUrl);
        } else {
          alert("Failed to load PDF");
          setPdfPreviewOpen(false);
        }
      } else {
        alert("PDF data not available");
        setPdfPreviewOpen(false);
      }
    } catch (error) {
      console.error("Error fetching MOU PDF:", error);
      alert(error.response?.data?.message || "Failed to load MOU PDF");
      setPdfPreviewOpen(false);
    } finally {
      setLoadingPdf(false);
    }
  };

  // Handle Download from Preview Modal
  const handleDownloadFromPreview = () => {
    if (pdfBase64) {
      const linkSource = `data:application/pdf;base64,${pdfBase64}`;
      const downloadLink = document.createElement("a");
      const fileName = `MOU_${currentMouNumber || currentMouId}.pdf`;

      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  // Close PDF Preview Modal
  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfBase64("");
    setCurrentMouNumber("");
    setCurrentMouId(null);

    // Cleanup blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl("");
    }
  };

  // Fetch available SPOCs for a specific MOU
  const fetchAvailableSpocs = async (mouId) => {
    setLoadingSpocs(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/available-spocs/${mouId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setAvailableSpocs(response.data.spocs || []);
    } catch (error) {
      console.error("Error fetching SPOCs:", error);
      setAvailableSpocs([]);
    } finally {
      setLoadingSpocs(false);
    }
  };

  // Handle opening popover for SPOC selection
  const handleOpenPopover = async (event, mouId) => {
    setAnchorEl(event.currentTarget);
    setSpocMouId(mouId);
    setSelectedEducatorId("");
    await fetchAvailableSpocs(mouId);
  };

  // Handle closing popover
  const handleClosePopover = () => {
    setAnchorEl(null);
    setSpocMouId(null);
    setSelectedEducatorId("");
    setAvailableSpocs([]);
  };

  // Handle sending MOU to institute
  const handleSendToInstitute = async () => {
    if (!selectedEducatorId || !spocMouId) {
      alert("Please select a SPOC");
      return;
    }

    setSendingMouId(spocMouId);
    try {
      const formData = new FormData();
      formData.append("mou_id", spocMouId);
      formData.append("selected_educator_id", selectedEducatorId);

      const response = await axios.post(
        `${BASE_URL}/accounts/send-to-institute`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      alert(response?.data?.message || "MOU sent successfully");
      handleClosePopover();
      await fetchMOUs();
    } catch (error) {
      console.error("Error sending MOU:", error);
      alert(error.response?.data?.message || "Failed to send MOU");
    } finally {
      setSendingMouId(null);
    }
  };

  const handleSignMOU = async (mouId) => {
    setSigningMouId(mouId);
    try {
      const formData = new FormData();

      const response = await axios.post(
        `${BASE_URL}/accounts/sign-mou/${mouId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      alert(response?.data?.message || "MOU signed successfully");
      await fetchMOUs();
    } catch (error) {
      console.error("Error signing MOU:", error);
    } finally {
      setSigningMouId(null);
    }
  };

  // Handle View Exhibit A
  const handleViewExhibitA = (content, mouId) => {
    setSelectedExhibitContent(content || "");
    setSelectedMouId(mouId);
    setExhibitAModalOpen(true);
  };

  // Handle Close Exhibit A Modal
  const handleCloseExhibitAModal = () => {
    setExhibitAModalOpen(false);
    setSelectedExhibitContent("");
    setSelectedMouId(null);
  };

  // Handle Exhibit A Updated
  const handleExhibitAUpdated = () => {
    fetchMOUs();
    handleCloseExhibitAModal();
  };

  const handleClose = () => {
    setMous([]);
    onClose();
  };

  const handleOpenCreateMOU = () => {
    setCreateMOUOpen(true);
  };

  const handleCloseCreateMOU = () => {
    setCreateMOUOpen(false);
  };

  const handleMOUCreated = () => {
    fetchMOUs();
    setCreateMOUOpen(false);
  };

  const renderStatusChip = (statusKey) => {
    const getStatusColor = () => {
      switch (statusKey) {
        case "account_signed":
          return { bg: "#DCFCE7", color: "#15803D" };
        case "institute_signed":
          return { bg: "#FEF3C7", color: "#92400E" };
        case "not_sent":
          return { bg: "#FEE2E2", color: "#991B1B" };
        default:
          return { bg: "#F3F4F6", color: "#6B7280" };
      }
    };

    const colors = getStatusColor();
    return (
      <Chip
        label={statusKey ? statusKey.replace(/_/g, " ") : "-"}
        size="small"
        sx={{
          fontSize: "11px",
          height: "24px",
          fontWeight: 500,
          backgroundColor: colors.bg,
          color: colors.color,
          border: "none",
          textTransform: "capitalize",
        }}
      />
    );
  };

  const renderActionCell = (mou) => {
    // Handle "not_sent" status - Show Sign Now button
    if (mou.status === "not_sent") {
      const isLoading = signingMouId === mou.mou_id;
      return (
        <Button
          variant="contained"
          size="small"
          disabled={isLoading}
          onClick={() => handleSignMOU(mou.mou_id)}
          sx={{
            backgroundColor: "#3B82F6",
            "&:hover": { backgroundColor: "#2563EB" },
            "&.Mui-disabled": {
              backgroundColor: "#93C5FD",
              color: "#FFFFFF",
            },
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            boxShadow: "none",
            minWidth: "85px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {isLoading && <AppleStyleSpinner />}
          {isLoading ? "Signing..." : "Sign Now"}
        </Button>
      );
    }

    // Handle "institute_signed" status - Show Sign Now button
    if (mou.status === "institute_signed" && mou.is_accounts_signed === false) {
      const isLoading = signingMouId === mou.mou_id;
      return (
        <Button
          variant="contained"
          size="small"
          disabled={isLoading}
          onClick={() => handleSignMOU(mou.mou_id)}
          sx={{
            backgroundColor: "#3B82F6",
            "&:hover": { backgroundColor: "#2563EB" },
            "&.Mui-disabled": {
              backgroundColor: "#93C5FD",
              color: "#FFFFFF",
            },
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            boxShadow: "none",
            minWidth: "85px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {isLoading && <AppleStyleSpinner />}
          {isLoading ? "Signing..." : "Sign Now"}
        </Button>
      );
    }

    // Handle "account_signed" status - Show Send button with SPOC selection
    if (mou.is_accounts_signed === true && mou.status === "account_signed") {
      const isSending = sendingMouId === mou.mou_id;
      return (
        <Button
          variant="contained"
          size="small"
          disabled={isSending}
          onClick={(e) => handleOpenPopover(e, mou.mou_id)}
          sx={{
            backgroundColor: "#8B5CF6",
            "&:hover": { backgroundColor: "#7C3AED" },
            "&.Mui-disabled": {
              backgroundColor: "#C4B5FD",
              color: "#FFFFFF",
            },
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            boxShadow: "none",
            minWidth: "85px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {isSending && <AppleStyleSpinner />}
          {isSending ? "Sending..." : "Send"}
        </Button>
      );
    }

    return (
      <Typography
        sx={{
          fontSize: "12px",
          color: "#6B7280",
        }}
      >
        -
      </Typography>
    );
  };

  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? "spoc-popover" : undefined;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 600, color: "#1F1F1F" }}
          >
            Pending MOUs
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateMOU}
              sx={{
                backgroundColor: "#3B82F6",
                "&:hover": { backgroundColor: "#2563EB" },
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                boxShadow: "none",
              }}
            >
              Create MOU
            </Button>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : mous.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                No pending MOUs found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#FAFAFA",
                      "& th": {
                        borderBottom: "1px solid #E5E7EB",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                      }}
                    >
                      MOU Number
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                      }}
                    >
                      Institute Signed At
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                      }}
                    >
                      Signer Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                      }}
                    >
                      Created At
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                        textAlign: "center",
                      }}
                    >
                      Exhibit A / MOU
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 1.5,
                        textAlign: "center",
                        width: "120px",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mous.map((mou, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                        "& td": {
                          borderBottom: "1px solid #F3F4F6",
                        },
                        "&:last-child td": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: "13px",
                          color: "#1F1F1F",
                          fontWeight: 500,
                          py: 2,
                        }}
                      >
                        {mou.mou_number || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "13px",
                          color: "#6B7280",
                          py: 2,
                        }}
                      >
                        {mou.institute_signed_at || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "13px",
                          color: "#6B7280",
                          py: 2,
                        }}
                      >
                        {mou.institute_signer_name || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "13px",
                          color: "#6B7280",
                          py: 2,
                        }}
                      >
                        {mou.created_at || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {renderStatusChip(mou.status)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          py: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() =>
                              handleViewExhibitA(
                                mou.exhibit_a_content,
                                mou.mou_id
                              )
                            }
                            sx={{
                              textTransform: "none",
                              fontSize: "11px",
                              fontWeight: 500,
                              color: "#3B82F6",
                              "&:hover": {
                                backgroundColor: "#EFF6FF",
                              },
                              minWidth: "auto",
                              px: 1.5,
                            }}
                          >
                            Exhibit A
                          </Button>
                          <Box
                            sx={{
                              width: "1px",
                              height: "16px",
                              backgroundColor: "#E5E7EB",
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleViewMOUPDF(mou.mou_id, mou.mou_number)
                            }
                            sx={{
                              color: "#10B981",
                              "&:hover": {
                                backgroundColor: "#D1FAE5",
                              },
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          py: 2,
                        }}
                      >
                        {renderActionCell(mou)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #E5E7EB",
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontSize: "13px",
              color: "#6B7280",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 600, color: "#1F1F1F" }}
          >
            MOU Preview - {currentMouNumber}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadFromPreview}
              disabled={!pdfBase64 || loadingPdf}
              sx={{
                backgroundColor: "#10B981",
                "&:hover": { backgroundColor: "#059669" },
                "&.Mui-disabled": {
                  backgroundColor: "#9CA3AF",
                  color: "#FFFFFF",
                },
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                boxShadow: "none",
              }}
            >
              Download
            </Button>
            <IconButton
              onClick={handleClosePdfPreview}
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#F9FAFB",
          }}
        >
          {loadingPdf ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                Loading PDF...
              </Typography>
            </Box>
          ) : pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title="MOU PDF Preview"
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                No PDF available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* SPOC Selection Popover */}
      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            mt: 1,
            minWidth: "320px",
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#1F1F1F",
              mb: 2,
            }}
          >
            Select SPOC
          </Typography>

          {loadingSpocs ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedEducatorId}
                  onChange={(e) => setSelectedEducatorId(e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: "13px",
                    "& .MuiSelect-select": {
                      py: 1.5,
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>
                          Choose a SPOC
                        </Typography>
                      );
                    }
                    const spoc = availableSpocs.find(
                      (s) => s.educator_id === selected
                    );
                    return spoc ? spoc.name : "";
                  }}
                >
                  {availableSpocs.length === 0 ? (
                    <MenuItem disabled>
                      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                        No SPOCs available
                      </Typography>
                    </MenuItem>
                  ) : (
                    availableSpocs.map((spoc) => (
                      <MenuItem
                        key={spoc.educator_id}
                        value={spoc.educator_id}
                        sx={{
                          fontSize: "13px",
                          py: 1.5,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#1F1F1F",
                            }}
                          >
                            {spoc.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#6B7280",
                              mt: 0.5,
                            }}
                          >
                            {spoc.designation}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#9CA3AF",
                              mt: 0.25,
                            }}
                          >
                            {spoc.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  onClick={handleClosePopover}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    color: "#6B7280",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendToInstitute}
                  variant="contained"
                  size="small"
                  disabled={!selectedEducatorId || sendingMouId !== null}
                  sx={{
                    backgroundColor: "#8B5CF6",
                    "&:hover": { backgroundColor: "#7C3AED" },
                    "&.Mui-disabled": {
                      backgroundColor: "#C4B5FD",
                      color: "#FFFFFF",
                    },
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 500,
                    boxShadow: "none",
                    minWidth: "80px",
                  }}
                >
                  {sendingMouId ? "Sending..." : "Send"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>

      <CreateMOUModal
        open={createMOUOpen}
        onClose={handleCloseCreateMOU}
        instituteId={instituteId}
        membershipId={membershipId}
        onSuccess={handleMOUCreated}
      />

      <ExhibitAModal
        open={exhibitAModalOpen}
        onClose={handleCloseExhibitAModal}
        content={selectedExhibitContent}
        mouId={selectedMouId}
        onSuccess={handleExhibitAUpdated}
      />
    </>
  );
};

export default ViewMOUModal;
